(ns ui.view.transaction-process.cards.property-list
  "Home of `field-row` — the scalar/keyword/currency value-field renderer reused
   by the generic field card."
  (:require
   [common.eid :as eid]
   [common.localization :as loc]
   [common.schema.display-name :as display-name]
   [common.schema.field :as field]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [ui.components.alert.view :as alert]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.modals.entity-selector.view :as entity-selector]
   [ui.view.transaction-process.event :as event]
   [ui.view.transaction-process.util :as tp-util]))

(defn read-only-field
  "Display-only renderer for a computed/calculated field: the same label as
   `field-row`, with the formatted value shown as plain text instead of a greyed
   disabled input. Currency values include their symbol (e.g. \"EUR 12\"); an
   uncomputable value (no components yet, or mixed currencies) shows nothing."
  [{:keys [field-kw field-config entity-data]}]
  (let [value (tp-util/format-field-value {:field-kw    field-kw
                                           :field-cfg   field-config
                                           :entity-data entity-data})]
    [:div {:class "flex flex-col gap-1.5"}
     [labelled-input/label-elem {:label (or (:field/label field-config)
                                            (tp-util/error-text
                                             {:context (str "Missing :field/label for " field-kw)
                                              :value   field-kw}))
                                 :class "min-h-9 flex items-end"}]
     ;; `py-2 text-md` mirrors the number input's padding + font size, so the
     ;; value lands at the same height as an editable field's text (tight to the
     ;; label, not floating) and the grid rows line up.
     [:div {:class "py-2 text-md m-0 text-gray-900 font-medium"}
      (when (cu/non-empty-string? value) value)]]))

(defn- ref->option
  "Materialize a stored ref value (db-id / {:db/id n}) into the {:value :label}
   shape the entity-search picker expects, resolving the display name from the
   session. Returns nil for a blank value."
  [value]
  (when (some? value)
    (let [id (eid/eid value)
          entity (session/get-entity id)]
      {:value id
       :label (if entity
                (display-name/compute {:entity entity})
                (str id))})))

(defn field-notice
  [{:keys [field-config entity-data]}]
  (when-let [{:notice/keys [text-key] :as notice-config}
             (:field/notice field-config)]
    (when (tp-util/field-visible? {:field-config notice-config
                                   :value-lookup entity-data})
      [alert/warning
       {}
       [:p {:class "m-0"}
        (loc/tr text-key)]])))

(defn field-row
  [{:keys [ctx field-kw field-config entity-data permission mandatory?]}]
  (let [label     (or (:field/label field-config)
                      (tp-util/error-text {:context (str "Missing :field/label for " field-kw)
                                           :value field-kw}))
        data-type (:field/data-type field-config)
        read-only? (= permission :r)
        value     (get entity-data field-kw)
        on-change (fn [new-val]
                    (rf/dispatch [::event/update-draft-field ctx field-kw new-val]))
        create!   (when (and (:create-on-fly? field-config)
                             (not read-only?))
                    (fn [query]
                      (entity-selector/open-and-select!
                       (field/create-entity-types field-config)
                       {:start-value       query
                        :attribute         field-kw
                        :skip-success-msg? true
                        :on-select!        (fn [new-entity-id _new-option]
                                             (on-change new-entity-id))})))
        ref?      (contains? #{:data.type/ref-single :data.type/ref-multi} data-type)
        options   (when (#{:data.type/keyword :data.type/keyword-multi} data-type)
                    (:field/options field-config))
        base-props (cond-> {:label      label
                            :data-type  data-type
                            :value      value
                            :on-change  on-change
                            :disabled?  read-only?
                            :mandatory? (boolean mandatory?)
                            :label-class "min-h-9 flex items-end"}
                     (:field/placeholder field-config)
                     (assoc :placeholder (:field/placeholder field-config))

                     (seq options)
                     (assoc :options options)

                     (and (= :data.type/percentage data-type)
                          (not (:field/uncapped-percentage? field-config)))
                     (assoc :min 0
                            :max 100))]
    (cond
      (= data-type :data.type/currency-value)
      (let [sym-kw (cu/currency->symbol field-kw)
            sym-val (get entity-data sym-kw)]
        [labelled-input/currency-view
         (assoc base-props
                :secondary-args {:data-type :data.type/currency-symbol
                                 :value     (or sym-val @session/default-currency)
                                 :disabled? read-only?
                                 :on-change (fn [v] (rf/dispatch [::event/update-draft-field ctx sym-kw v]))})])

      ref?
       ;; Ref fields render as an entity-search picker (CRM company/person lookup).
       ;; The picker needs the value as a {:value :label} option and the entity
       ;; types to search; on-change stores the selected entity id (ref-multi: ids).
      (let [multi?       (= data-type :data.type/ref-multi)
            picker-value (if multi?
                           (->> (or value []) (keep ref->option) vec)
                           (ref->option value))]
        [labelled-input/view
         (merge base-props
                {:editor-type               :editor.type/search
                 :value                     picker-value
                 :field/select-entity-types (or (:field/select-entity-types field-config)
                                                (:field/entity-types field-config))
                 :create!                   create!
                 :on-change                 (fn [selected & _]
                                              (rf/dispatch [::event/update-draft-field ctx field-kw selected]))}
                (select-keys field-config
                             [:field/option-filter-fn
                              :field/os-filter-terms-fn]))])

      :else
      [labelled-input/view base-props])))
